// src/core/score/etwDealScore.ts
// Bruecke zwischen den ETW-Mechanik-Kennzahlen (core/metrics/etw.ts) und dem
// analyzer-uebergreifenden Deal-Score (dealScore.ts). Zwei Varianten:
//
// - fromMetrics(): fuer die lebende Analyzer-Seite (Eigentumswohnung.tsx), die
//   computeEtwMetrics() ohnehin schon aufgerufen hat -- vermeidet eine doppelte
//   Berechnung, nur der Stresstest braucht einen zweiten Aufruf.
// - fromSnapshotInput(): fuer "Meine Deals" und den PDF-Export, die nur die
//   gespeicherten Rohdaten (EtwSnapshot.input, inkl. Kaufnebenkosten-Felder, die
//   NICHT Teil des getypten EtwSnapshotInput sind, siehe etwSnapshot.ts) haben und
//   computeEtwMetrics() selbst aufrufen muessen.

import { computeEtwMetrics, type EtwMetrics, type EtwMetricsInput } from "../metrics/etw.js";
import { computeDealScore, type DealScoreResult, type LageInput } from "./dealScore.js";
import { ETW_SCORE_CONFIG } from "./dealScoreConfig.js";
import { NK_DEFAULT_PCTS } from "../../lib/purchaseCosts.js";
import { eigenkapitalFromLegacyLoan } from "../financing.js";
import type { EtwSnapshotInput, EtwSnapshotPlayground } from "../../lib/etwSnapshot.js";

/** Migration (16.10.2026, einheitliche Darlehensregel): ein alter ETW-Snapshot/-Export
 *  (vor der Umstellung von ltvPct auf eigenkapital) speicherte Kredit = All-in x ltvPct
 *  (ETW war der einzige der 5 Typen, der auf All-in statt Kaufpreis basierte -- finanzierte
 *  dadurch implizit auch die Nebenkosten). Aequivalentes Eigenkapital ueber die gemeinsame
 *  Migrations-Funktion (core/financing.ts), die auch den (bei hoher Beleihung durchaus
 *  haeufigen) Randfall Kredit_alt > Kaufpreis korrekt behandelt (siehe dort). */
export function etwEigenkapitalFromLegacyLtv(kaufpreis: number, nkPct: number, nkRenovierung: number, nkSanierung: number, ltvPct: number): number {
  const allIn = kaufpreis * (1 + nkPct) + nkRenovierung + nkSanierung;
  const kreditAlt = allIn * ltvPct;
  return eigenkapitalFromLegacyLoan(allIn, kreditAlt);
}

/** Loest das Eigenkapital aus einem (ggf. alten) ETW-Snapshot-Input auf -- wendet
 *  etwEigenkapitalFromLegacyLtv() an, wenn legacyLtvPct gesetzt ist, sonst der direkte
 *  Wert. */
export function resolveEtwEigenkapital(
  rawInput: { kaufpreis: number; eigenkapital: number; legacyLtvPct: number | null; nkRenovierung: number; nkSanierung: number },
  nkPct: number
): number {
  return rawInput.legacyLtvPct == null
    ? rawInput.eigenkapital
    : etwEigenkapitalFromLegacyLtv(rawInput.kaufpreis, nkPct, rawInput.nkRenovierung, rawInput.nkSanierung, rawInput.legacyLtvPct);
}

const STRESS_ZINS_DELTA = 0.01;
const STRESS_MIETE_FAKTOR = 0.9;

function toDealScoreInput(
  metrics: EtwMetrics,
  stressMetrics: EtwMetrics,
  financingOn: boolean,
  lage: LageInput,
  vermietungsstatus: "vollstaendig" | "teilweise" | "nein"
) {
  return {
    kaufpreis: metrics.kaufpreisView,
    gesamtinvestition: metrics.allIn,
    jahreskaltmiete: metrics.grossRentYear,
    noiJahr: metrics.noi,
    annuitaetJahr: metrics.annuitaetJahr,
    financingOn,
    stressNoiJahr: stressMetrics.noi,
    stressAnnuitaetJahr: stressMetrics.annuitaetJahr,
    lage,
    vermietungsstatus,
  };
}

/** Fuer die lebende Analyzer-Seite: metrics ist das Ergebnis des bereits erfolgten computeEtwMetrics()-Aufrufs. */
export function computeEtwDealScoreFromMetrics(
  baseInput: EtwMetricsInput,
  metrics: EtwMetrics,
  lage: LageInput,
  vermietungsstatus: "vollstaendig" | "teilweise" | "nein" = "vollstaendig"
): DealScoreResult {
  const stressInput: EtwMetricsInput = {
    ...baseInput,
    zinsPct: baseInput.zinsPct + STRESS_ZINS_DELTA,
    mieteProM2Monat: baseInput.mieteProM2Monat * STRESS_MIETE_FAKTOR,
    // Stress-Pass-Fix (18.10.2026): Hausgeld-Anteil + Instandhaltung auf Basis-Miete fix,
    // siehe mfhDealScore.ts.
    pauschaleBasisGrossRentYear: metrics.grossRentYear,
  };
  const stressMetrics = computeEtwMetrics(stressInput);
  return computeDealScore(toDealScoreInput(metrics, stressMetrics, baseInput.financingOn, lage, vermietungsstatus), ETW_SCORE_CONFIG);
}

/** Fuer "Meine Deals"/PDF: nur die gespeicherten Rohdaten vorhanden, kein live computeEtwMetrics()-Ergebnis. */
export function computeEtwDealScoreFromSnapshotInput(
  rawInput: EtwSnapshotInput & Record<string, unknown>,
  playground: EtwSnapshotPlayground,
  lage: LageInput,
  vermietungsstatus: "vollstaendig" | "teilweise" | "nein" = "vollstaendig"
): DealScoreResult {
  const base: EtwMetricsInput = {
    kaufpreis: rawInput.kaufpreis,
    flaecheM2: rawInput.flaecheM2,
    mieteProM2Monat: rawInput.mieteProM2Monat,
    leerstandPct: rawInput.leerstandPct,
    opexPctBrutto: rawInput.opexPctBrutto,
    instandhaltungPct: rawInput.instandhaltungPct,
    sondereigentumRuecklage: rawInput.sondereigentumRuecklage,
    // Kaufnebenkosten: nicht Teil von EtwSnapshotInput (eigenes Subsystem, siehe
    // etwSnapshot.ts), liegen aber als Spread von exportPurchaseCosts() im rohen
    // gespeicherten Objekt -- mit den aktuellen Defaults als Fallback.
    nkGrEStPct: typeof rawInput.nkGrEStPct === "number" ? rawInput.nkGrEStPct : NK_DEFAULT_PCTS.grest,
    nkNotarPct: typeof rawInput.nkNotarPct === "number" ? rawInput.nkNotarPct : NK_DEFAULT_PCTS.notar,
    nkGrundbuchPct: typeof rawInput.nkGrundbuchPct === "number" ? rawInput.nkGrundbuchPct : NK_DEFAULT_PCTS.grundbuch,
    nkMaklerPct: typeof rawInput.nkMaklerPct === "number" ? rawInput.nkMaklerPct : NK_DEFAULT_PCTS.makler,
    nkSonstPct: typeof rawInput.nkSonstPct === "number" ? rawInput.nkSonstPct : NK_DEFAULT_PCTS.sonstiges,
    nkRenovierung: rawInput.nkRenovierung,
    nkSanierung: rawInput.nkSanierung,
    financingOn: rawInput.financingOn,
    eigenkapital: resolveEtwEigenkapital(
      rawInput as any,
      (typeof rawInput.nkGrEStPct === "number" ? rawInput.nkGrEStPct : NK_DEFAULT_PCTS.grest) +
        (typeof rawInput.nkNotarPct === "number" ? rawInput.nkNotarPct : NK_DEFAULT_PCTS.notar) +
        (typeof rawInput.nkGrundbuchPct === "number" ? rawInput.nkGrundbuchPct : NK_DEFAULT_PCTS.grundbuch) +
        (typeof rawInput.nkMaklerPct === "number" ? rawInput.nkMaklerPct : NK_DEFAULT_PCTS.makler) +
        (typeof rawInput.nkSonstPct === "number" ? rawInput.nkSonstPct : NK_DEFAULT_PCTS.sonstiges)
    ),
    zinsPct: rawInput.zinsPct,
    tilgungPct: rawInput.tilgungPct,
    priceAdjPct: playground.priceAdjPct,
    rentAdjPct: playground.rentAdjPct,
    applyAdjustments: playground.applyAdjustments,
    capRatePct: rawInput.capRatePct,
  };
  const metrics = computeEtwMetrics(base);
  return computeEtwDealScoreFromMetrics(base, metrics, lage, vermietungsstatus);
}
